import React, {useMemo, useState} from "react";
import {Box, Stack, Typography, ToggleButtonGroup, ToggleButton, Collapse, ButtonBase, Tooltip, useMediaQuery} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {ComposedChart, Bar, ReferenceLine, Tooltip as ChartTooltip, XAxis, YAxis, ResponsiveContainer} from "recharts";
import dayjs from "dayjs";
import {RangeCaption} from "./KpiStrip";
import {HelpPopover} from "./cells";
import {
    fmtMoneyShort, fmtMoneyCompact, fmtSignedPct, fmtCompact, fmtPct0, fmtDay, fmtDow, fmtRange, shortProvider, EMPTY, isNilValue,
    COMPARE_BASES, MINUS,
} from "./format";
import {TYPE, TABULAR, NEUTRAL, CHART, WARNING_TEXT} from "./tokens";

export const SERIES = [{key: "rev", label: "rev"}, {key: "net", label: "net"}];
export const readSeries = () => {
    try {
        const v = window.localStorage.getItem("tc.overviewSeries");
        if (SERIES.some((s) => s.key === v)) return v;
    } catch (e) { /* ignore */ }
    return "rev";
};

export const OVERVIEW_HELP = [
    "Headline: OK = nothing new broke · CHECK = something needs a look (listed below) · UNKNOWN = partners still to report cover too much revenue to judge.",
    "Yesterday = last closed UTC day. p36 of 28 d = better than 36% of the last 28 days; σ = distance from their mean.",
    "7-day = mean of the last 7 closed days with spike days replaced by the 28-day median (ex-spike), so a two-day burst neither reads as growth nor its end as a slide. Raw figures in the tooltip.",
    "Month = net to date; projected = ex-spike run-rate to raw run-rate. Net = revenue × our share.",
    "Compare control: every 'Δ yday' on the page follows it (7-day median or day before).",
];

const GREY = "rgba(0,0,0,.38)";
const STATE2 = {OK: {color: "success.dark", word: "OK"}, CHECK: {color: WARNING_TEXT, word: "CHECK"}, UNKNOWN: {color: GREY, word: "UNKNOWN"}};
const TREND_WORD = {SLIDING: "Sliding", GROWING: "Growing", SOFT_DAY: "Soft day", STEADY: "Steady", UNKNOWN: "Unknown"};
const trendColor = (state) => ({SLIDING: "error.main", GROWING: "success.dark", SOFT_DAY: "warning.dark"}[state] || "text.secondary");
/** The 7-day delta is coloured only when the verdict itself is about the week (GROWING / SLIDING). */
const weekDeltaColor = (state, v) => (state === "GROWING" || state === "SLIDING" ? (Number(v) >= 0 ? "success.dark" : "error.main") : GREY);
const dayDeltaColor = (state) => (state === "SOFT_DAY" ? "warning.dark" : GREY);

const median = (xs) => {
    const s = xs.filter((x) => !isNilValue(x)).map(Number).sort((a, b) => a - b);
    if (!s.length) return null;
    const m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/** "$4.3k" / "$820" for the chart's ▲ ▼ labels. */
const fmtK = (v) => (isNilValue(v) ? EMPTY : Math.abs(v) >= 1000 ? `$${(Number(v) / 1000).toFixed(1)}k` : fmtMoneyShort(v));

/** "vs Aug 1–13": the previous month's window with the same number of elapsed days. */
const prevWindowLabel = (asOf, daysElapsed) => {
    if (!asOf || !daysElapsed) return null;
    const start = dayjs.utc(asOf).startOf("month").subtract(1, "month");
    const end = start.add(Math.min(daysElapsed, start.daysInMonth()) - 1, "day");
    return fmtRange(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
};

/** `$65–72k`: ex-spike to raw projection (one figure when they agree within 2 %). */
const projectionText = (exSpike, raw) => {
    if (isNilValue(exSpike)) return fmtMoneyCompact(raw);
    if (isNilValue(raw) || Math.abs(Number(raw) - Number(exSpike)) <= 0.02 * Math.abs(Number(exSpike))) return fmtMoneyCompact(exSpike);
    const lo = Math.min(Number(exSpike), Number(raw));
    const hi = Math.max(Number(exSpike), Number(raw));
    const k = (v) => (Math.abs(v) >= 1e4 ? `${(v / 1e3).toFixed(1)}k` : fmtMoneyShort(v).replace("$", ""));
    return `$${k(lo)}–${k(hi)}`;
};

const toggleSx = (h = 24) => ({
    "& .MuiToggleButton-root": {
        height: h, px: h < 24 ? 0.75 : 1, fontSize: h < 24 ? 11 : 11.5, fontWeight: 600, textTransform: "none", lineHeight: 1, ...TABULAR,
        color: "text.secondary", borderColor: NEUTRAL.divider,
        "&.Mui-selected": {color: "primary.main", bgcolor: "rgba(25,118,210,.08)"},
    },
});

// ---------------------------------------------------------------------------------------------------------------------

const Tip = ({active, payload}) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <Box sx={{bgcolor: "background.paper", border: `1px solid ${NEUTRAL.divider}`, borderRadius: 1, px: 1, py: 0.5, fontSize: 11.5, ...TABULAR, boxShadow: 1, whiteSpace: "nowrap"}}>
            <Typography component="div" sx={{fontSize: 11.5, fontWeight: 600}}>
                {fmtDay(d.day)} {fmtDow(d.day)}
                {d.settled === false && <Box component="span" sx={{color: "text.secondary", fontWeight: 400}}> · ◌ not yet reported</Box>}
                {d.spike && <Box component="span" sx={{color: "text.secondary", fontWeight: 400}}> · spike day (counted as the 28-day median in the verdict)</Box>}
            </Typography>
            <Typography component="div" sx={{fontSize: 11.5}}>rev {fmtMoneyShort(d.rev)} · net {fmtMoneyShort(d.net)} · sent {fmtCompact(d.fs)}</Typography>
        </Box>
    );
};

/** Bar shape: alpha .35; spike days hatched; the last bar alpha .5 + dashed stroke while not reported. */
const BarShape = (props) => {
    const {x, y, width, height, payload} = props;
    if (!(width > 0) || !(height > 0)) return null;
    const hollow = payload.last && payload.settled === false;
    return (
        <g>
            {hollow && <title>not yet reported</title>}
            {payload.spike && <title>spike day</title>}
            <rect x={x} y={y} width={width} height={height} rx={1} fill={payload.spike ? "url(#tc-spike-hatch)" : CHART.bar} fillOpacity={payload.spike ? 1 : hollow ? 0.5 : 0.35}
                  stroke={hollow ? CHART.bar : "none"} strokeWidth={hollow ? 1 : 0} strokeDasharray={hollow ? "3 2" : undefined}/>
        </g>
    );
};

/**
 * 30 daily bars of `rev` or `net` on a truncated y-domain [min×0.9, max×1.05] with ▲/▼ labels at the right; spike days
 * hatched; dotted 28-day median; no axes, no legend. `width` null = responsive.
 */
const TrendChart = ({daily, series, median28, spikeDays, width, height}) => {
    const spikes = useMemo(() => new Set(Array.isArray(spikeDays) ? spikeDays : []), [spikeDays]);
    const data = useMemo(() => daily.map((d, i) => ({...d, v: Number(d[series]) || 0, spike: spikes.has(d.day) || d.spike === true, last: i === daily.length - 1})), [daily, series, spikes]);
    const ref = useMemo(() => (series === "rev" && !isNilValue(median28) ? Number(median28) : median(data.slice(-28).map((d) => d.v))), [series, median28, data]);
    const {lo, hi} = useMemo(() => {
        const vs = data.map((d) => d.v).filter((v) => v > 0);
        if (!vs.length) return {lo: 0, hi: 1};
        return {lo: Math.min(...vs) * 0.9, hi: Math.max(...vs) * 1.05};
    }, [data]);
    const n = data.length;
    const chart = (
        <ComposedChart width={width || undefined} height={height} data={data} margin={{top: 1, right: 0, bottom: 1, left: 0}} barCategoryGap={n > 20 ? 1 : 2}>
            <defs>
                <pattern id="tc-spike-hatch" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <rect width="4" height="4" fill={CHART.bar} fillOpacity="0.25"/>
                    <line x1="0" y1="0" x2="0" y2="4" stroke={CHART.bar} strokeWidth="1.5"/>
                </pattern>
            </defs>
            <XAxis dataKey="day" hide/>
            <YAxis hide domain={[lo, hi]} allowDataOverflow/>
            <ChartTooltip content={<Tip/>} cursor={{fill: "rgba(0,0,0,.04)"}} isAnimationActive={false} wrapperStyle={{zIndex: 3}} allowEscapeViewBox={{x: false, y: true}}/>
            {ref !== null && <ReferenceLine y={ref} stroke="rgba(0,0,0,.6)" strokeDasharray="1 3" strokeWidth={1} ifOverflow="extendDomain"/>}
            <Bar dataKey="v" name={series} isAnimationActive={false} minPointSize={1} shape={<BarShape/>}/>
        </ComposedChart>
    );
    const vs = data.map((d) => d.v).filter((v) => v > 0);
    const max = vs.length ? Math.max(...vs) : null;
    const min = vs.length ? Math.min(...vs) : null;
    return (
        <Box role="img" aria-label={`${n} days of ${series}: ${fmtK(min)}–${fmtK(max)}`}
             sx={{display: "flex", alignItems: "stretch", width: width ? width + 44 : "100%", height, minWidth: 0}}>
            <Box sx={{flex: width ? "0 0 auto" : "1 1 auto", minWidth: 0, width: width || "auto"}}>
                {width ? chart : <ResponsiveContainer width="100%" height={height}>{chart}</ResponsiveContainer>}
            </Box>
            <Box aria-hidden="true" sx={{flex: "0 0 44px", display: "flex", flexDirection: "column", justifyContent: "space-between", pl: 0.5, ...TABULAR}}>
                <Typography component="span" sx={{fontSize: 10, lineHeight: 1, color: "text.secondary", whiteSpace: "nowrap"}}>▲ {fmtK(max)}</Typography>
                <Typography component="span" sx={{fontSize: 10, lineHeight: 1, color: "text.secondary", whiteSpace: "nowrap"}}>▼ {fmtK(min)}</Typography>
            </Box>
        </Box>
    );
};

// ---------------------------------------------------------------------------------------------------------------------

const WEEK_DELTA = /([+\-−]\d+(?:\.\d+)?%)(?= vs prior week)/;

/** A clause of the trend sentence with the week delta figure coloured per the rule; amber for Tiger / not-reported clauses. */
const Clause = ({text, state}) => {
    const amber = /Tiger data missing|not yet reported|not reported/i.test(text);
    const color = amber ? WARNING_TEXT : "text.secondary";
    const m = WEEK_DELTA.exec(text);
    if (!m) return <Box component="span" sx={{color}}>{amber && /not yet reported/i.test(text) ? "◌ " : ""}{text}</Box>;
    const i = m.index;
    return (
        <Box component="span" sx={{color}}>
            {text.slice(0, i)}
            <Box component="span" sx={{fontWeight: 600, color: weekDeltaColor(state, m[1].replace(MINUS, "-").replace("%", ""))}}>{m[1]}</Box>
            {text.slice(i + m[1].length)}
        </Box>
    );
};

/** `CHECK — Bidberry stopped Sep 13: $1,086/d (5 tags) · 1 partner event` with the verdict word coloured. */
const Headline = ({trend, size = 14, lines = 1}) => {
    const t = trend || {};
    const state2 = STATE2[t.state2] || STATE2.UNKNOWN;
    const text = String(t.headline || t.sentence || EMPTY);
    const m = /^(OK|CHECK|Not enough data yet)\s*(—|·)?\s*/.exec(text);
    const word = m ? (m[1] === "Not enough data yet" ? "UNKNOWN" : m[1]) : state2.word;
    const rest = m ? text.slice(m[0].length) : text;
    const body = m && m[1] === "Not enough data yet" ? `not enough data yet · ${rest}` : rest;
    return (
        <Typography component="span" title={text} sx={{fontSize: size, fontWeight: 600, lineHeight: 1.35, minWidth: 0, flex: "0 1 auto", color: "text.primary", ...TABULAR,
            ...(lines === 1 ? {whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"} : {display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden"})}}>
            <Box component="span" sx={{color: state2.color, textTransform: "uppercase", letterSpacing: "0.03em"}}>{word}</Box>
            <Box component="span" sx={{color: "text.disabled", mx: 0.75}}>—</Box>
            {body}
        </Typography>
    );
};

const Dot = ({color, size = 8, sx}) => (
    <Box component="span" aria-hidden="true" sx={{display: "inline-block", width: size, height: size, borderRadius: "50%", bgcolor: color, flex: "0 0 auto", ...sx}}/>
);

/** One KPI block: eyebrow · value + delta · up to two sub-lines (nodes). */
const Kpi = ({label, value, valueTip, deltaText, deltaColor, deltaTip, sub, sub2, first, last}) => {
    const val = <Typography component="span" sx={{...TYPE.kpiValue}}>{value}</Typography>;
    const delta = deltaText && <Typography component="span" sx={{fontSize: 12, fontWeight: 600, ...TABULAR, color: deltaColor || GREY}}>{deltaText}</Typography>;
    return (
        <Box sx={{display: "flex", flexDirection: "column", justifyContent: "flex-start", flex: "0 0 auto", pl: first ? 0 : 2, pr: last ? 0 : 2.5, borderLeft: first ? 0 : `1px solid ${NEUTRAL.divider}`, minHeight: 72, "& > *": {whiteSpace: "nowrap"}}}>
            <Typography component="span" sx={{...TYPE.eyebrow, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 10.5, mb: "1px"}}>{label}</Typography>
            <Box sx={{display: "flex", alignItems: "baseline", columnGap: 1}}>
                {valueTip ? <Tooltip title={valueTip} placement="top" arrow enterDelay={400}>{val}</Tooltip> : val}
                {delta && (deltaTip ? <Tooltip title={deltaTip} placement="top" arrow enterDelay={400}>{delta}</Tooltip> : delta)}
            </Box>
            <Box sx={{fontSize: 12, color: "text.secondary", ...TABULAR, lineHeight: "20px", display: "flex", alignItems: "center", columnGap: 0.75, minHeight: 20}}>{sub}</Box>
            {sub2 && <Typography component="span" sx={{fontSize: 11.5, color: "text.secondary", ...TABULAR, lineHeight: 1.2}}>{sub2}</Typography>}
        </Box>
    );
};

/** `−0.9% vs [7d avg | day before]` — the compare control lives inside the Yesterday block. */
const CompareControl = ({value, onChange}) => (
    <ToggleButtonGroup exclusive size="small" value={value} onChange={(e, v) => v && onChange(v)} aria-label="Yesterday compared with" sx={toggleSx(20)}>
        {COMPARE_BASES.map((b) => <ToggleButton key={b.key} value={b.key} aria-label={`Compare yesterday with ${b.label}`}>{b.label}</ToggleButton>)}
    </ToggleButtonGroup>
);

/** "not yet reported: Admaven, Bidgency +2" / "all partners reported". */
const reportedLine = (pending) => {
    if (!pending.length) return "all partners reported";
    const names = pending.map(shortProvider);
    return `not yet reported: ${names.slice(0, 2).join(", ")}${names.length > 2 ? ` +${names.length - 2}` : ""}`;
};

const YESTERDAY_TIP = "Only partners that have reported yesterday; the 7-day base is their own median of the 7 days before. p = rank among the last 28 days; σ = distance from their mean. ◌ = not yet reported by the partner.";

/** While partners are pending, the like-for-like delta needs its caveat and the raw total next to it. */
const pendingTip = (y, pending) => {
    if (!pending.length) return null;
    const usual = isNilValue(y.pending_usual_usd) ? "" : ` (usually ~${fmtMoneyShort(y.pending_usual_usd)}/d together)`;
    const raw = !isNilValue(y.rev_all) && !isNilValue(y.rev_d2_all)
        ? ` All partners as loaded now: ${fmtMoneyShort(y.rev_all)} vs ${fmtMoneyShort(y.rev_d2_all)} the day before${isNilValue(y.delta_d1_all_pct) ? "" : ` (${fmtSignedPct(y.delta_d1_all_pct)})`} — that raw figure will move as they land.`
        : "";
    return `${pending.length} partner${pending.length === 1 ? "" : "s"} not yet reported: ${pending.map(shortProvider).join(", ")}${usual}. The % compares only the partners that have reported both days (like-for-like).${raw}`;
};

/** `normal day (p36)` / `weak day (p4)` / `strong day (p96)` — plain words first; the σ lives in the tooltip. */
const controlText = (y) => {
    if (isNilValue(y.z) && isNilValue(y.pct)) return "";
    const z = isNilValue(y.z) ? 0 : Number(y.z);
    const word = z <= -1.5 ? "weak day" : z >= 1.5 ? "strong day" : z <= -0.75 ? "softer day" : z >= 0.75 ? "better day" : "normal day";
    return `${word}${isNilValue(y.pct) ? "" : ` (p${y.pct})`}`;
};
const controlTip = (y) => (isNilValue(y.z) ? YESTERDAY_TIP : `${YESTERDAY_TIP} Yesterday: ${Number(y.z) > 0 ? "+" : Number(y.z) < 0 ? MINUS : ""}${Math.abs(Number(y.z)).toFixed(2)}σ from the 28-day mean${isNilValue(y.pct) ? "" : `, better than ${y.pct}% of those days`}.`);

/**
 * Overview band (v3). Reads `overview` (as of UTC yesterday, never driven by the range picker). Desktop: headline + trend
 * clause, then KPIs + 420×72 chart. Tablet (< 1100): chart full-width under the KPIs. Phone: headline (2 lines) +
 * Yesterday; the rest in a Collapse. `series` (rev | net) is owned by the page (the toggle sits in the table toolbar).
 * `children` = the today line (rendered under the headline / inside the phone band).
 */
const roundPct = (v) => (isNilValue(v) ? EMPTY : `${Number(v) > 0 ? "+" : Number(v) < 0 ? MINUS : ""}${Math.round(Math.abs(Number(v)))}%`);

/** `◌ 4 partners still to report (≈ $720/d) · re-sync [· all partners $2,300 (−7%)]` — the raw figure only when it differs from the like-for-like one. */
const PendingLine = ({y, pending, onResync, phone}) => {
    const differs = !isNilValue(y.rev_all) && !isNilValue(y.rev) && Math.abs(Number(y.rev_all) - Number(y.rev)) > 1;
    return (
        <Box component="span" title={pendingTip(y, pending) || undefined} sx={{color: WARNING_TEXT, whiteSpace: phone ? "normal" : "nowrap"}}>
            ◌ {pending.length} partner{pending.length === 1 ? "" : "s"} still to report{isNilValue(y.pending_usual_usd) ? "" : ` (≈ ${fmtMoneyShort(y.pending_usual_usd)}/d)`}
            {onResync && (
                <>
                    {" · "}
                    <Box component="button" type="button" onClick={(e) => { e.stopPropagation(); onResync(pending.length === 1 ? pending[0] : null, null); }} aria-label="Re-sync a partner for one day"
                         sx={{border: 0, p: 0, bgcolor: "transparent", font: "inherit", color: "primary.main", fontWeight: 600, cursor: "pointer", "&:hover": {textDecoration: "underline"}}}>re-sync</Box>
                </>
            )}
            {differs && <Box component="span" sx={{color: "text.secondary"}}> · all partners {fmtMoneyShort(y.rev_all)}{isNilValue(y.delta_d1_all_pct) ? "" : ` (${roundPct(y.delta_d1_all_pct)} vs day before)`}</Box>}
        </Box>
    );
};

const readBandOpen = () => { try { return window.sessionStorage.getItem("tc.band.open") === "1"; } catch (e) { return false; } };

const OverviewBand = ({overview, summary, range, days, compareBase, onCompareBase, phone = false, rowCount, cutoffCount, zeroCount, series = "rev", children, onResync, showYesterday = false}) => {
    const [open, setOpenState] = useState(readBandOpen);
    const setOpen = (updater) => setOpenState((prev) => { const v = typeof updater === "function" ? updater(prev) : updater; try { window.sessionStorage.setItem("tc.band.open", v ? "1" : "0"); } catch (e) { /* ignore */ } return v; });
    const narrow = useMediaQuery("(max-width:1099px)", {noSsr: true});
    const o = overview || {};
    const daily = Array.isArray(o.daily) ? o.daily : [];
    const y = o.yesterday || {};
    const m = o.month || {};
    const trend = o.trend || {};
    const state = trend.state;
    const pending = Array.isArray(y.pending_providers) ? y.pending_providers : [];
    const prevLabel = prevWindowLabel(o.as_of, m.days_elapsed);
    const spikeDays = Array.isArray(o.spike_days) ? o.spike_days : [];
    const take = isNilValue(o.take_pct) ? null : Number(o.take_pct) / 100;
    const net = series === "net";
    const asOfRow = daily.length ? daily[daily.length - 1] : null;
    const yValue = net ? (asOfRow && asOfRow.day === o.as_of ? asOfRow.net : take !== null && !isNilValue(y.rev) ? Number(y.rev) * take : null) : y.rev;
    const yDelta = compareBase === "d1" ? y.delta_d1_pct : y.delta_base_pct;
    const ma7 = net ? o.ma7_net : o.ma7;                 // ex-spike (v3: `ma7` IS the ex-spike figure; raw kept as ma7_raw)
    const ma7Raw = net ? o.ma7_net_raw : o.ma7_raw;
    const weekDelta = o.ma7_delta_pct;
    const weekText = isNilValue(weekDelta) ? null : Math.abs(Number(weekDelta)) < 1 ? "flat vs prior week" : `${roundPct(weekDelta)} vs prior week`;   // same rounding as the trend clause
    // Spike days named only when they sit inside the window a figure describes (the last 7 days, the month to date).
    const spike7 = o.as_of ? spikeDays.filter((d) => d >= dayjs.utc(o.as_of).subtract(6, "day").format("YYYY-MM-DD")) : spikeDays;
    const spikeMonth = o.as_of ? spikeDays.filter((d) => d >= dayjs.utc(o.as_of).startOf("month").format("YYYY-MM-DD")) : spikeDays;
    const rawTip = spikeDays.length
        ? `Raw${spike7.length ? ` (incl. spike day${spike7.length === 1 ? "" : "s"} ${spike7.map(fmtDay).join(", ")})` : ""}: ${fmtMoneyShort(ma7Raw)}/d${isNilValue(o.ma7_delta_pct_raw) ? "" : `, ${fmtSignedPct(o.ma7_delta_pct_raw)} vs prior week`}`
        : null;
    const monthLabel = o.as_of ? dayjs.utc(o.as_of).format("MMM").toUpperCase() : "MONTH";
    const netSuffix = net ? " · net" : "";
    const clauses = String(trend.clause || "").split(" · ").filter(Boolean);
    const trendWord = TREND_WORD[state] || null;
    const monthDeltaEx = isNilValue(m.delta_pct_ex) ? m.delta_pct : m.delta_pct_ex;
    const monthDeltaText = isNilValue(monthDeltaEx) ? null : `${fmtSignedPct(monthDeltaEx)}${spikeDays.length && !isNilValue(m.delta_pct_ex) ? " ex-spike" : ""}${prevLabel ? ` vs ${prevLabel}` : ""}`;
    const monthTip = spikeMonth.length && !isNilValue(m.delta_pct) ? `${fmtSignedPct(m.delta_pct)} incl. the spike day${spikeMonth.length === 1 ? "" : "s"} ${spikeMonth.map(fmtDay).join(", ")}` : null;
    const projection = projectionText(m.projected_net, m.projected_net_raw);

    if (!overview) return null;

    const weekSub = (
        <>
            {trendWord && <Box component="span" sx={{fontWeight: 600, color: trendColor(state)}}>{trendWord}</Box>}
            {spikeDays.length ? <> · ex-spike{!isNilValue(ma7Raw) ? ` · raw ${fmtMoneyShort(ma7Raw)}/d` : ""}</> : net ? <> · revenue {fmtMoneyShort(o.ma7)}/d</> : <> · net {fmtMoneyShort(o.ma7_net)}/d{take !== null ? ` (${fmtPct0(take)} ours)` : ""}</>}
        </>
    );

    if (phone) {
        const state2 = STATE2[trend.state2] || STATE2.UNKNOWN;
        const headText = String(trend.headline || trend.sentence || EMPTY);
        const m1 = /^(OK|CHECK|Not enough data yet)\s*(—|·)?\s*/.exec(headText);
        const word = m1 ? (m1[1] === "Not enough data yet" ? "UNKNOWN" : m1[1]) : state2.word;
        const rest = m1 ? headText.slice(m1[0].length) : headText;
        return (
            <Box component="section" aria-label="Overview" sx={{boxSizing: "border-box", ...TABULAR}}>
                {/* Row 2 (40 px): the verdict in one line; tap = the full band. The tab row below stays sticky, this line scrolls away. */}
                <ButtonBase component="div" role="button" tabIndex={0} aria-expanded={open} aria-label="Toggle overview details"
                            onClick={() => setOpen((v) => !v)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }}
                            sx={{display: "flex", alignItems: "center", width: "100%", textAlign: "left", px: 1.5, height: 40, gap: 0.75, boxSizing: "border-box"}}>
                    <Dot color={state2.color} size={7}/>
                    <Typography component="span" noWrap title={headText} sx={{fontSize: 13, minWidth: 0, flex: "1 1 auto", lineHeight: 1.3}}>
                        <Box component="span" sx={{fontWeight: 600, color: state2.color}}>{word}</Box>
                        <Box component="span" sx={{color: "text.secondary"}}> · {rest}</Box>
                    </Typography>
                    {/* Yesterday's $ pinned at the right unless the sentence already starts with it (OK days do). */}
                    {!/^nothing big broke · yesterday \$/.test(rest) && <Typography component="span" sx={{fontSize: 13, fontWeight: 600, ...TABULAR, flex: "0 0 auto"}}>{fmtMoneyShort(yValue)}</Typography>}
                    <ExpandMoreIcon sx={{fontSize: 18, color: "text.secondary", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", flex: "0 0 auto"}}/>
                </ButtonBase>
                {showYesterday && !open && (
                    <Box sx={{px: 1.5, pb: 0.75, mt: -0.5}}>
                        <Typography component="div" noWrap sx={{fontSize: 11, color: "text.secondary", ...TABULAR, lineHeight: "20px"}}>
                            <Box component="span" sx={{fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 10}}>Yesterday {fmtDay(o.as_of)}</Box>
                            {" · "}<Box component="span" sx={{fontWeight: 600, color: dayDeltaColor(state)}}>{fmtSignedPct(yDelta)}</Box> vs {compareBase === "d1" ? "day before" : "7d"}{pending.length ? " · same partners" : ""}
                            {controlText(y) ? ` · ${controlText(y)}` : ""}
                        </Typography>
                        {pending.length > 0 && (
                            <Typography component="div" sx={{fontSize: 11, ...TABULAR, lineHeight: "18px"}}>
                                <PendingLine y={y} pending={pending} onResync={onResync} phone/>
                            </Typography>
                        )}
                        {children}
                    </Box>
                )}
                <Collapse in={open}>
                    <Box sx={{px: 1.5, pb: 1.25, pt: 0.25, borderBottom: `1px solid ${NEUTRAL.hairline}`}}>
                        <Headline trend={trend} size={13.5} lines={3}/>
                        <Box sx={{display: "flex", alignItems: "baseline", columnGap: 1, mt: 0.75, flexWrap: "wrap"}}>
                            <Typography component="span" sx={{...TYPE.eyebrow, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em"}}>Yesterday ({fmtDay(o.as_of)}){netSuffix}</Typography>
                            <Typography component="span" sx={{fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.2, ...TABULAR}}>{fmtMoneyShort(yValue)}</Typography>
                            <Typography component="span" sx={{fontSize: 11, fontWeight: 600, ...TABULAR, color: dayDeltaColor(state)}}>{fmtSignedPct(yDelta)} vs {compareBase === "d1" ? "day before" : "7d"}{pending.length ? " · same partners" : ""}</Typography>
                            <Typography component="span" sx={{fontSize: 11, color: "text.secondary", ...TABULAR}}>{controlText(y)}</Typography>
                            {pending.length > 0 && (
                                <Typography component="span" sx={{fontSize: 11, ...TABULAR, flex: "1 0 100%"}}>
                                    <PendingLine y={y} pending={pending} onResync={onResync} phone/>
                                </Typography>
                            )}
                        </Box>
                        {children}
                        <Typography component="div" sx={{fontSize: 12, color: "text.secondary", lineHeight: 1.4, ...TABULAR, mt: 0.5}}>
                            {clauses.map((p, i) => <React.Fragment key={i}>{i > 0 && " · "}<Clause text={p} state={state}/></React.Fragment>)}
                        </Typography>
                        <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 1, mt: 0.75}}>
                            {[
                                {label: `7-day${netSuffix}${spikeDays.length ? " · ex-spike" : ""}`, value: `${fmtMoneyShort(ma7)}/d`, delta: weekText || "", color: weekDeltaColor(state, weekDelta), tip: rawTip},
                                {label: `${monthLabel} net`, value: fmtMoneyCompact(m.mtd_net), delta: monthDeltaText || "", color: GREY, tip: monthTip},
                            ].map((k) => (
                                <Box key={k.label} sx={{minWidth: 0}} title={k.tip || undefined}>
                                    <Typography component="span" noWrap sx={{...TYPE.eyebrow, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", display: "block"}}>{k.label}</Typography>
                                    <Typography component="span" sx={{fontSize: 17, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.2, ...TABULAR, display: "block"}}>{k.value}</Typography>
                                    <Typography component="span" noWrap sx={{fontSize: 10.5, fontWeight: 600, ...TABULAR, color: k.color, display: "block"}}>{k.delta}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Typography component="div" sx={{...TYPE.caption, fontSize: 12, mt: 0.5, ...TABULAR}}>
                            {pending.length ? `◌ not yet reported: ${pending.map(shortProvider).join(", ")}` : "all partners reported"}
                            {prevLabel && <> · projected {projection}{monthTip ? ` (${monthTip})` : ""}</>}
                        </Typography>
                        <Box sx={{height: 40, width: "100%", mt: 0.75}} onTouchStart={(e) => e.stopPropagation()}>
                            <TrendChart daily={daily} series={series} median28={o.median28} spikeDays={spikeDays} height={40}/>
                        </Box>
                        <RangeCaption s={summary} range={range} days={days} count={rowCount} cutoffCount={cutoffCount} zeroCount={zeroCount} wrap sx={{whiteSpace: "normal", lineHeight: 1.4, mt: 0.75}}/>
                    </Box>
                </Collapse>
            </Box>
        );
    }

    const chart = <TrendChart daily={daily} series={series} median28={o.median28} spikeDays={spikeDays} width={narrow ? null : 420} height={narrow ? 48 : 72}/>;

    return (
        <Box component="section" aria-label="Overview"
             sx={{borderTop: `1px solid ${NEUTRAL.hairline}`, borderBottom: `1px solid ${NEUTRAL.hairline}`, py: 0.75, boxSizing: "border-box", ...TABULAR}}>
            {/* Line 1: dot + headline (one line; the full text in the title) + trend clause; `?` at the right. */}
            <Stack direction="row" alignItems={narrow ? "flex-start" : "center"} spacing={1} sx={{minHeight: 24, minWidth: 0}}>
                <Dot color={(STATE2[trend.state2] || STATE2.UNKNOWN).color} sx={narrow ? {mt: "6px"} : undefined}/>
                <Box sx={{flex: "1 1 auto", minWidth: 0, display: "flex", alignItems: "baseline", columnGap: 1.5, flexWrap: narrow ? "wrap" : "nowrap"}}>
                    <Headline trend={trend} lines={narrow ? 2 : 1}/>
                    {/* Tablet: the clause takes its own row (2 lines max); desktop: the rest of the line, ellipsised. */}
                    <Typography component="span" noWrap={!narrow} title={trend.clause}
                                sx={{fontSize: 12, color: "text.secondary", minWidth: narrow ? 0 : 140, flex: narrow ? "1 0 100%" : "1 1 0",
                                    ...(narrow ? {display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4} : {})}}>
                        {clauses.map((p, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <Box component="span" sx={{mx: 0.75, color: "text.disabled"}}>·</Box>}
                                <Clause text={p} state={state}/>
                            </React.Fragment>
                        ))}
                    </Typography>
                </Box>
                <HelpPopover label="Overview" lines={OVERVIEW_HELP}/>
            </Stack>
            {children}

            <Box sx={{display: "flex", alignItems: "flex-start", flexWrap: narrow ? "wrap" : "nowrap", mt: 0.5, rowGap: 0.75, minWidth: 0}}>
                <Kpi first label={`Yesterday (${fmtDay(o.as_of)})${netSuffix}${pending.length ? " · so far" : ""}`} value={fmtMoneyShort(yValue)} valueTip={pending.length ? pendingTip(y, pending) : YESTERDAY_TIP}
                     deltaText={controlText(y) || null} deltaColor={GREY} deltaTip={controlTip(y)}
                     sub={<><Box component="span" sx={{fontWeight: 600, color: dayDeltaColor(state)}}>{fmtSignedPct(yDelta)}</Box> vs <CompareControl value={compareBase} onChange={onCompareBase}/>{pending.length > 0 && <Box component="span" sx={{color: "text.secondary"}} title="Only the partners that have reported both days">· same partners</Box>}</>}
                     sub2={pending.length ? <PendingLine y={y} pending={pending} onResync={onResync}/> : <Box component="span">{reportedLine(pending)}</Box>}/>
                <Kpi label={`7-day${netSuffix}`} value={`${fmtMoneyShort(ma7)}/d`}
                     deltaText={weekText} deltaColor={weekDeltaColor(state, weekDelta)} deltaTip={rawTip}
                     sub={weekSub}/>
                <Kpi last={narrow} label={`${monthLabel} net to date`} value={fmtMoneyCompact(m.mtd_net)}
                     deltaText={monthDeltaText} deltaColor={GREY} deltaTip={monthTip}
                     sub={`projected ${projection} · day ${m.days_elapsed || EMPTY} of ${m.days_in_month || EMPTY}`}/>
                {narrow ? (
                    <Box sx={{flex: "1 0 100%", minWidth: 0, height: 48}}>{chart}</Box>
                ) : (
                    <Box sx={{flex: "0 0 auto", ml: "auto", pl: 2, borderLeft: `1px solid ${NEUTRAL.divider}`, display: "flex", alignItems: "center", height: 72}}>{chart}</Box>
                )}
            </Box>
        </Box>
    );
};

export default OverviewBand;
export {CompareControl, toggleSx};

/** `[rev | net]` — which money the band shows; the page owns the state (table toolbar). */
export const SeriesToggle = ({value, onChange}) => (
    <ToggleButtonGroup exclusive size="small" value={value} onChange={(e, v) => v && onChange(v)} aria-label="Money shown" sx={toggleSx()}>
        {SERIES.map((s) => <ToggleButton key={s.key} value={s.key} aria-label={`Show ${s.key === "net" ? "net" : "revenue"}`}>{s.label}</ToggleButton>)}
    </ToggleButtonGroup>
);
